import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { useVariablesStore } from '../store/variablesStore';
import { saveWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { writeMirror } from '../services/workspaceMirror';
import { usesLocalDataPath, isGitWorkspace } from '../utils/workspaceStorage';
import { syncEngineEnabled } from '../utils/featureFlags';

/**
 * Debounced workspace-persistence writers extracted verbatim from App (same
 * effects, dep arrays and gating). All three early-return until the current
 * workspace's data is hydrated (`snapshotHydratedRef.current === workspace.id`)
 * so a fresh load window never clobbers on-disk data:
 *  1. Local-workspace snapshot save (skipEmptyArrays).
 *  2. YAML mirror auto-write for every non-git workspace (git workspaces persist
 *     their YAML at rootPath already, so writeMirror skips them).
 *  3. Cloud-workspace schema-tree auto-persist (local uses the snapshot writer).
 *
 * @param {{
 *   buildLiveSnapshot: Function,
 *   persistSchemas: Function,
 *   snapshotHydratedRef: { current: any },
 * }} deps
 */
export function useWorkspacePersistence({ buildLiveSnapshot, persistSchemas, snapshotHydratedRef }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const collections = useCollectionsStore((s) => s.collections);
  const schemas = useCollectionsStore((s) => s.schemas);
  const globalVariables = useVariablesStore((s) => s.globalVariables);
  const workspaceVariables = useVariablesStore((s) => s.workspaceVariables);
  const environments = useVariablesStore((s) => s.environments);
  const selectedEnvironment = useVariablesStore((s) => s.selectedEnvironment);

  // Local-workspace snapshot save.
  useEffect(() => {
    if (!currentWorkspace || !usesLocalDataPath(currentWorkspace, isAuthenticated)) return;
    if (snapshotHydratedRef.current !== currentWorkspace.id) return;

    const delay = isGitWorkspace(currentWorkspace) ? 400 : 0;
    const timer = setTimeout(() => {
      saveWorkspaceSnapshot(
        currentWorkspace,
        {
          workspaceName: currentWorkspace.name || '',
          collections,
          globalVariables,
          workspaceVariables,
          environments,
          schemas,
          selectedEnvironmentId: selectedEnvironment?.id || '',
        },
        { skipEmptyArrays: true }
      ).catch(() => {});
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the listed fields are the ones that pick the destination; the whole object churns on every save (updated_at), which would make this writer retrigger itself
  }, [
    currentWorkspace?.id,
    currentWorkspace?.rootPath,
    currentWorkspace?.storage,
    collections,
    globalVariables,
    workspaceVariables,
    environments,
    schemas,
    selectedEnvironment?.id,
    isAuthenticated,
    snapshotHydratedRef,
  ]);

  // Continuously mirror EVERY workspace (local + cloud) to a YAML folder on disk
  // so a real diff-able tree always exists for git sync. Reads the in-memory
  // snapshot only — does not touch the sidebar tree or DnD logic. Git-storage
  // workspaces already persist their YAML at rootPath, so writeMirror skips them.
  useEffect(() => {
    if (!currentWorkspace?.id) return undefined;
    if (isGitWorkspace(currentWorkspace)) return undefined;
    // Phase 4: flag-enrolled workspaces get their mirror from the SQLite git
    // flush (workspaceStoreRouter → gitFlush.js) — the zustand-driven mirror
    // effect only remains for non-enrolled workspaces.
    if (syncEngineEnabled(currentWorkspace)) return undefined;
    // Only write once the current workspace's data is hydrated, so we never
    // clobber a fresh mirror with stale/empty collections during a switch.
    if (snapshotHydratedRef.current !== currentWorkspace.id) return undefined;

    const timer = setTimeout(() => {
      writeMirror(currentWorkspace, buildLiveSnapshot()).catch(() => {});
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- same reason as the snapshot writer above: depending on the whole workspace object would make each mirror write schedule the next one
  }, [
    currentWorkspace?.id,
    currentWorkspace?.storage,
    currentWorkspace?.name,
    collections,
    globalVariables,
    workspaceVariables,
    environments,
    schemas,
    selectedEnvironment?.id,
    buildLiveSnapshot,
    snapshotHydratedRef,
  ]);

  // Auto-persist schema tree edits for cloud workspaces (local uses snapshot effect).
  useEffect(() => {
    if (!currentWorkspace || usesLocalDataPath(currentWorkspace, isAuthenticated)) return;
    if (snapshotHydratedRef.current !== currentWorkspace.id) return;

    const timer = setTimeout(() => {
      persistSchemas(schemas).catch(() => {});
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id + storage decide whether this workspace persists to the cloud; the rest of the object churns on every save
  }, [currentWorkspace?.id, currentWorkspace?.storage, schemas, isAuthenticated, persistSchemas, snapshotHydratedRef]);
}
